<template>
  <footer class="bg-brand-darker container-padding-x">
    <div class="max-w-7xl mx-auto py-24">
      <div class="flex flex-row flex-wrap">
        <div class="w-full md:w-5/12 lg:w-4/12">
          <h3 class="font-semibold text-3xl text-white">Subscribe to get weekly news, articles and listings</h3>

          <UtilMessage v-if="newsletterApi.status === 'success'" type="success" class="mt-4">
            Thank for you for subscribing to our newsletter
          </UtilMessage>

          <Form
            v-else
            @submit="subscribeToNewsletter()"
            ref="form"
            v-slot="{ formErrors, formSubmissionAttempted }"
          >
            <FormErrorReport
              class="mt-4"
              :formSubmissionAttempted="formSubmissionAttempted"
              :formErrors="formErrors"
            />

            <UtilApiError :models="[newsletterApi]" />

            <div class="mt-4 flex items-center sm:max-w-md">
              <FormTextInput
                type="email"
                name="email"
                v-model="newsletterApi.model.email"
                placeholder="Enter your email"
                rules="required|email"
                :showValidationError="false"
                class="mb-0 flex w-full email-input-subscribe"
                targetClass="flex flex-grow-1 border-none"
              />

              <UtilButton
                :loading="newsletterApi.loading"
                type="submit"
                color="primary"
                class="sm:mt-0 rounded-l-none flex font-medium"
                style="height: 43px"
              >
                Join Us
              </UtilButton>
            </div>
          </Form>
        </div>

        <div class="w-full md:w-7/12 md:pl-40 flex flex-wrap mt-6 md:mt-0">
          <div class="w-1/2 sm:w-3/12 md:w-1/2 lg:w-3/12">
            <h4 class="font-normal text-gray-200">Services</h4>

            <ul class="mt-1">
              <li>
                <UtilTextLink to="/search/livestock" class="sublink-text no-underline">
                  Buy &amp; sell livestock
                </UtilTextLink>
              </li>

              <li>
                <UtilTextLink to="/search/feed-bedding" class="sublink-text no-underline">
                  Feed &amp; bedding
                </UtilTextLink>
              </li>

              <li>
                <UtilTextLink to="/membership-plans" class="sublink-text no-underline">Membership</UtilTextLink>
              </li>
            </ul>
          </div>

          <div class="w-1/2 sm:w-3/12 md:w-1/2 lg:w-3/12">
            <h4 class="font-normal text-gray-200">Support</h4>

            <ul class="mt-1">
              <li>
                <UtilTextLink to="/help/how-it-works" class="sublink-text no-underline">
                  Help &amp; FAQ
                </UtilTextLink>
              </li>

              <li>
                <UtilTextLink to="/app-download" class="sublink-text no-underline">Download app</UtilTextLink>
              </li>

              <li>
                <UtilTextLink to="/sitemap" class="sublink-text no-underline">Sitemap</UtilTextLink>
              </li>
            </ul>
          </div>

          <div class="w-1/2 sm:w-3/12 md:w-1/2 lg:w-3/12 pt-6 sm:pt-0 md:pt-6 lg:pt-0">
            <h4 class="font-normal text-gray-200">Company</h4>

            <ul class="mt-1">
              <li>
                <UtilTextLink to="/blog" class="sublink-text no-underline">Blog &amp; news</UtilTextLink>
              </li>

              <li>
                <UtilTextLink to="/about" class="sublink-text no-underline">About</UtilTextLink>
              </li>
            </ul>
          </div>

          <div class="w-1/2 sm:w-3/12 md:w-1/2 lg:w-3/12 pt-6 sm:pt-0 md:pt-6 lg:pt-0">
            <h4 class="font-normal text-gray-200">Legal</h4>

            <ul class="mt-1">
              <li>
                <UtilTextLink to="/terms" class="sublink-text no-underline">Terms &amp; privacy</UtilTextLink>
              </li>

              <li>
                <UtilTextLink to="/manage-cookies" class="sublink-text no-underline">Manage cookies</UtilTextLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="container pb-12">
      <div class="flex flex-col-reverse sm:flex-row flex-wrap items-center justify-between">
        <span class="text-white text-sm w-full sm:w-auto mt-2 sm:mt-0">
          &copy; SellMyLivestock {{ new Date().getFullYear() }}. All rights reserved.

          <span :class="appIs.isOutOfDate ? 'text-red-400' : 'text-white'">
            v{{ $store.state.device.version }}
          </span>
        </span>

        <div class="flex items-center text-4xl w-full sm:w-auto">
          <a
            href="https://www.facebook.com/sellmylivestock"
            target="_blank"
            rel="noopener noreferrer"
            class="text-gray-200 hover:text-gray-100"
          >
            <FontAwesomeIcon :icon="faFacebookF" />
          </a>

          <a
            href="https://twitter.com/sellmylivestock"
            target="_blank"
            rel="noopener noreferrer"
            class="ml-6 text-gray-200 hover:text-gray-100"
          >
            <FontAwesomeIcon :icon="faTwitter" />
          </a>

          <a
            href="https://www.instagram.com/sellmylivestock"
            target="_blank"
            rel="noopener noreferrer"
            class="ml-6 text-gray-200 hover:text-gray-100"
          >
            <FontAwesomeIcon :icon="faInstagram" />
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
  import {faApple, faFacebookF, faInstagram, faTwitter} from '@fortawesome/free-brands-svg-icons'
  import Api from '@/lib/api'
  import UtilTextLink from '@/components/utils/UtilTextLink'
  import UtilButton from '@/components/utils/UtilButton'
  import FormTextInput from '@/components/forms/elements/FormTextInput'
  import FormErrorReport from '@/components/forms/elements/FormErrorReport'
  import Form from '@/components/forms/elements/Form'
  import UtilApiError from '@/components/utils/UtilApiError'
  import UtilMessage from '@/components/utils/UtilMessage'

  export default {
  name: 'LayoutFooterNew',
  components: {
    UtilButton,
    UtilTextLink,
    FormTextInput,
    FormErrorReport,
    Form,
    UtilApiError,
    UtilMessage
  },
  data() {
    return {
      newsletterApi: new Api({
        email: ''
      }),
      faApple,
      faFacebookF,
      faTwitter,
      faInstagram
    }
  },
  methods: {
    subscribeToNewsletter() {
      this.newsletterApi.post('/notify/v1/slack', {
        message: `Newsletter signup - ${this.newsletterApi.model.email}`,
        channels: ['smlsocialmedia']
      })
    }
  }
}
</script>

<style lang="scss">
.app-button {
  height: 35px;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
}

.email-input-subscribe {
  > div {
    @apply flex-grow;
    > div {
      @apply flex-grow;
      .form-input {
        @apply flex-grow;
      }
    }
  }
  .form-input {
    @apply rounded-r-none;
  }
}

.sublink-text.text-link {
  @apply text-gray-100 font-light text-sm leading-snug;
  &:hover {
    @apply text-gray-200;
  }
}
</style>
